import React, { useMemo } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button, Checkbox, IconButton, Stack, TableCell, TableRow, Typography } from "@mui/material";
import { useAiDataContext } from "components/AiData/context/AiData-context";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useFormatNumericHook } from "hooks";
import { IMeasureGroupOneInvestor, IRecommendationItem } from "store/types";

import { currencySign } from "../../../../constants";
import { CellWithTooltip } from "../CellWithTooltip";
import { ParameterRow } from "./ParameterRow";

const bodyCellStyle = {
  fontSize: "16px",
  fontWeight: "400",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  position: "relative",
  maxWidth: "140px"
};

interface Props {
  parameter: IMeasureGroupOneInvestor;
  hasParentWrapper?: boolean;
  dealId: number;
  fileId: number;
}

export const GroupedRow: React.FC<Props> = ({ parameter, hasParentWrapper, dealId, fileId }) => {
  const {
    isRecommendationWithChildrenFilled,
    toggleRowExpansion,
    expandedRows,
    generateUniqueId,
    onGroupRowApprove,
    allChildAccepted,
    userHasEditPermission,
    isUniqueIdSelected,
    isUniqueIdPartiallySelected,
    handleHierarchicalCheckboxChange,
    getMissingRequiredFields
  } = useAiDataContext();

  const formatNumeric = useFormatNumericHook();

  const missingFields = useMemo(() => {
    const childrenMissingFields = parameter.children?.map((child) =>
      getMissingRequiredFields(child as IRecommendationItem)
    );
    return new Set((childrenMissingFields ?? []).flat());
  }, [parameter.children]);

  return (
    <React.Fragment>
      <TableRow
        sx={{
          borderBottom: "1px solid #DBE8F5",
          "& td": {
            borderBottom: "1px solid #DBE8F5"
          },
          "& td:last-child > *": {
            opacity: 0,
            transition: "opacity 0.2s ease-in-out"
          },
          "&:hover td:last-child > *": {
            opacity: 1
          }
        }}
      >
        <TableCell sx={{ padding: "3px !important" }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              paddingLeft: hasParentWrapper ? "30px !important" : "0px !important"
            }}
          >
            <IconButton onClick={() => toggleRowExpansion(generateUniqueId(parameter, fileId))} sx={{ padding: "4px" }}>
              {expandedRows.has(generateUniqueId(parameter, fileId)) ? (
                <ExpandLessIcon sx={{ fontSize: "16px" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "16px" }} />
              )}
            </IconButton>
            <Checkbox
              disabled={!userHasEditPermission}
              checked={fileId ? isUniqueIdSelected(generateUniqueId(parameter, fileId), parameter, fileId) : false}
              indeterminate={fileId ? isUniqueIdPartiallySelected(parameter, fileId) : false}
              onChange={(event) =>
                fileId &&
                handleHierarchicalCheckboxChange(
                  generateUniqueId(parameter, fileId),
                  event.target.checked,
                  parameter,
                  fileId
                )
              }
              sx={{
                padding: "4px",
                "&:hover": {
                  backgroundColor: "transparent"
                }
              }}
            />
            <MaterialTooltip title={parameter.measure?.name}>
              <Typography
                noWrap
                style={{
                  display: "block",
                  width: "100%",
                  maxWidth: "200px"
                }}
              >
                {parameter.measure?.name || ""}
              </Typography>
            </MaterialTooltip>
          </Stack>
        </TableCell>
        <CellWithTooltip value={parameter.investor?.name || ""} style={{ ...bodyCellStyle, maxWidth: "150px" }} />
        <CellWithTooltip
          currency={currencySign[parameter.currency || ""]}
          value={
            parameter.children?.length
              ? formatNumeric({ value: parameter.value, format: { abbreviated: true, decimal: 2 } })
              : parameter.value
          }
          style={bodyCellStyle}
        />
        <CellWithTooltip
          value={parameter.date ? dayjs(parameter.date).format("DD/MM/YYYY") : ""}
          style={bodyCellStyle}
        />
        <CellWithTooltip value={parameter.reference || ""} style={{ ...bodyCellStyle, maxWidth: "150px" }} />
        <CellWithTooltip value={parameter.classification || ""} style={bodyCellStyle} />
        <TableCell />

        <TableCell sx={{ width: "100px" }}>
          {userHasEditPermission && (
            <MaterialTooltip
              title={
                !isRecommendationWithChildrenFilled(parameter)
                  ? `Missing required fields (${Array.from(missingFields).join(", ")})`
                  : ""
              }
              isShow
            >
              <span>
                <Button
                  onClick={() => {
                    fileId &&
                      parameter.measure?.id &&
                      (parameter.measure?.is_global_value ? true : parameter.investor?.id) &&
                      parameter.date &&
                      onGroupRowApprove({
                        file_id: fileId,
                        measure_id: parameter.measure.id,
                        ...(parameter.investor?.id ? { investor_id: parameter.investor.id } : {}),
                        date: parameter.date
                      });
                    // parameter.id && onChangeRecommendationStatus(RecommendationStatus.accepted, parameter.id)
                  }}
                  variant="contained"
                  color="primary"
                  disabled={!isRecommendationWithChildrenFilled(parameter) || allChildAccepted(parameter)}
                >
                  Approve
                </Button>
              </span>
            </MaterialTooltip>
          )}
        </TableCell>
      </TableRow>
      {expandedRows.has(generateUniqueId(parameter, fileId)) &&
        parameter.children?.map((child: IRecommendationItem | IMeasureGroupOneInvestor) => {
          if (child.children?.length) {
            return (
              <GroupedRow
                key={generateUniqueId(child, fileId)}
                parameter={child as IMeasureGroupOneInvestor}
                hasParentWrapper={true}
                dealId={dealId}
                fileId={fileId}
              />
            );
          }
          return (
            <ParameterRow
              key={generateUniqueId(child, fileId)}
              parameter={child as IRecommendationItem}
              dealId={dealId}
              nestedLevel={hasParentWrapper ? "groupedByMeasureAndInvestor" : "groupedByMeasure"}
              fileId={fileId}
            />
          );
        })}
    </React.Fragment>
  );
};
